import {
	useActiveFocuses,
	useDispatch,
	useRecurringOn,
	useScheduledOn,
	useToday
} from "@mapcraft-tasks/client-store/react";
import { isTerminal } from "@mapcraft-tasks/domain";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useToggleDone } from "../lib/complete";
import { Checkbox } from "./checkbox";
import { DateBadge } from "./date-badge";
import { DayNav } from "./day-nav";

/**
 * Панели справа. От того, что открыто в основном поле, они НЕ зависят: можно сидеть
 * в проекте и видеть свои сегодняшние сроки (ui-spec Область 5).
 *
 * Дедупа между панелями и основным полем нет и не нужно — списки раздельные и лежат
 * в разных местах экрана, сливать нечего.
 */
export function Panels({ drawer, onNavigate }: { drawer?: boolean; onNavigate?: () => void } = {}) {
	const today = useToday();
	const [date, setDate] = useState(today);

	return (
		/*
		 * Вторая панель в стеке, а не всплывающее окно — решение Tana для «настроек»:
		 * обе панели живые, подложка не затемняется, ничего не перекрывается.
		 *
		 * На телефоне колонки нет: панели уезжают в ту же шторку, что и навигация,
		 * и становятся её нижней секцией — своего фона и скроллера там не нужно.
		 */
		<aside
			className={
				drawer
					? "flex flex-col border-t border-stroke-soft"
					: "panel-aside flex h-full w-72 shrink-0 flex-col overflow-y-auto"
			}
		>
			<ScheduledPanel date={date} onDate={setDate} onNavigate={onNavigate} />
			<RecurringPanel date={date} onNavigate={onNavigate} />
			<FocusPanel onNavigate={onNavigate} />
		</aside>
	);
}

function ScheduledPanel({
	date,
	onDate,
	onNavigate
}: {
	date: string;
	onDate: (d: string) => void;
	onNavigate?: () => void;
}) {
	const items = useScheduledOn(date);
	const toggleDone = useToggleDone();

	return (
		<section className="border-b border-stroke-soft px-4 py-4">
			{/* Та же листалка, что в шапке страницы дня, — здесь с датой средним сегментом:
			    отдельному полю рядом в 288px колонки места нет. `flex-wrap` оставлен
			    страховкой на совсем узкую шторку телефона. */}
			<header className="mb-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
				<span className="section-label">Срок</span>
				<DayNav date={date} onDate={onDate} picker />
			</header>

			{items.length === 0 ? (
				<p className="text-sm font-light text-ink-muted">на эту дату ничего</p>
			) : (
				<ul>
					{/* Показываются ВСЕ ноды с этой датой, включая терминальные, и больше ничего. */}
					{items.map((node) => (
						<li
							key={node.id}
							className="-mx-2 flex items-center gap-[6px] rounded-sm px-2 transition-colors hover:bg-active"
						>
							{/* Отметить срок прямо здесь: список сроков и есть то место, где на них
							    смотрят, и ходить ради галочки в саму ноду незачем. */}
							<Checkbox node={node} className="self-center" onToggle={() => toggleDone(node)} />
							<Link
								to="/n/$nodeId"
								params={{ nodeId: node.id }}
								onClick={onNavigate}
								className={`nav-item min-w-0 flex-1 truncate py-px text-sm ${
									isTerminal(node.status) ? "text-ink-faint line-through" : "text-ink"
								}`}
							>
								{node.title || "без названия"}
							</Link>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}

/**
 * Фокус — то, чем занят сейчас: брат-близнец пинов, отдельный системный контейнер,
 * держит только вплетения. Показывается ВЕРХНИЙ УРОВЕНЬ и ничего больше: список,
 * в который нужно всматриваться, фокусом быть перестаёт.
 *
 * Живёт в правой колонке, а не в сайдбаре: слева — навигация по структуре, справа —
 * то, что делается сегодня, и фокус здесь стоит ровно в этом ряду.
 */
function FocusPanel({ onNavigate }: { onNavigate?: () => void }) {
	const items = useActiveFocuses();

	return (
		<section className="px-4 py-4">
			{/* Заголовок — он же вход в историю: в панели висят только открытые заходы,
			    а `/focus` показывает и закрытые. Стрелка нужна, чтобы это было видно:
			    без неё заголовок не читается как ссылка. */}
			<div className="section-label mb-2">
				<Link
					to="/focus"
					onClick={onNavigate}
					title="все фокусы, включая закрытые"
					className="inline-flex items-baseline gap-1 transition-colors hover:text-ink"
				>
					Фокус <span aria-hidden>›</span>
				</Link>
			</div>

			{items.length === 0 ? (
				<p className="text-sm font-light text-ink-muted">пусто — аккорд `rf` делает ноду фокусом</p>
			) : (
				<ul>
					{items.map((node) => (
						<li
							key={node.id}
							className="-mx-2 flex items-center gap-[6px] rounded-sm px-2 transition-colors hover:bg-active"
						>
							{/* Галочки здесь нет НАМЕРЕННО, даже места под неё: закрывают заход
							    на его странице или на `/focus`, а панель — только напоминание,
							    чем человек занят. Случайный клик по галочке в углу экрана
							    закрывал бы неделю работы. */}
							<Link
								to="/n/$nodeId"
								params={{ nodeId: node.id }}
								onClick={onNavigate}
								className="nav-item min-w-0 flex-1 truncate py-px text-sm text-ink"
							>
								{node.title || "без названия"}
							</Link>
							{/* Срок прижат к правому краю: в панели важно не «что», а «когда». */}
							<DateBadge node={node} />
						</li>
					))}
				</ul>
			)}
		</section>
	);
}

function RecurringPanel({ date, onNavigate }: { date: string; onNavigate?: () => void }) {
	const items = useRecurringOn(date);
	const toggleDone = useToggleDone();
	const dispatch = useDispatch();

	return (
		<section className="border-b border-stroke-soft px-4 py-4">
			<div className="section-label mb-2">Повторяющиеся</div>
			{items.length === 0 ? (
				<p className="text-sm font-light text-ink-muted">на эту дату нет активных</p>
			) : (
				<ul className="space-y-2">
					{items.map(({ node, progress }) => (
						<li key={node.id}>
							<div className="-mx-2 flex items-center gap-[6px] rounded-sm px-2 transition-colors hover:bg-active">
								{/*
								 * Главное, ради чего панель вообще открыта: отметить «сделал», не
								 * разыскивая ноду в дереве — а при цели «5 раз в день» разыскивать
								 * пришлось бы пять раз. Зачёт идёт НА ДАТУ ПАНЕЛИ, а не на сегодня:
								 * панель умеет смотреть на другой день, и галочка обязана попасть
								 * в тот же период, прогресс которого тут же и показан.
								 *
								 * Галочка закрашена по набранному x/N, а не по статусу: у
								 * повторяющейся он всегда `open`.
								 */}
								<Checkbox
									node={node}
									done={progress.complete}
									className="self-center"
									title="отметить выполнение"
									onToggle={() => toggleDone(node, date)}
								/>
								<Link
									to="/n/$nodeId"
									params={{ nodeId: node.id }}
									onClick={onNavigate}
									className={`nav-item min-w-0 flex-1 truncate py-px text-sm ${
										progress.complete ? "text-ink-faint" : "text-ink"
									}`}
								>
									{node.title || "без названия"}
								</Link>
								{/*
								 * Прогресс «x/N» считается на лету: материализации повторов нет.
								 * Он же и кнопка отмены: галочка тут добавляет событие, а не
								 * переключает статус, и промах иначе остался бы в счёте навсегда —
								 * отменить его нечем, `Cmd+Z` живёт в аутлайне.
								 */}
								{progress.done > 0 ? (
									<button
										type="button"
										title="убрать последнее выполнение"
										className="tabular shrink-0 text-2xs font-bold text-positive transition-colors hover:text-danger"
										onClick={() =>
											dispatch({ name: "uncompleteNode", input: { nodeId: node.id, on: date } })
										}
									>
										{progress.done}/{progress.target}
									</button>
								) : (
									<span className="tabular shrink-0 text-2xs font-bold text-positive">
										{progress.done}/{progress.target}
									</span>
								)}
							</div>
							{/* Полоса 6px прямо под строкой — маркер прогресса без отдельного блока
							    статистики; ровно рецепт `NodeProgressBar` из разбора. */}
							<div className="progress mt-1">
								<div
									className="progress-fill"
									style={{
										width: `${Math.min(100, Math.round((progress.done / Math.max(1, progress.target)) * 100))}%`
									}}
								/>
							</div>
						</li>
					))}
				</ul>
			)}
		</section>
	);
}
